import * as _ from 'lodash';
import * as moment from 'moment';

import {
    DashboardReadResultDto,
    DateValues,
    KeyValuePair,
    SerieValues,
    TagDateValue,
    WidgetQueryResults,
    WidgetReadConfiguration,
    WidgetReadResult,
    WidgetValues,
    WidgetValuesReadConfiguration
} from '../../../models';
import { IReadSeriesConfiguration } from '../../../widgets';
import { toDuration } from '../../../services';
import { toDictionary } from '../../../common';

function getKey(d: TagDateValue): string {
    return d.guid + d.valueType + d.timeStamp
        + (d.offset && d.offset.interval ? d.offset.interval + d.offset.intervalTimeUnit : '');
}

function toTagDateValue(dateValues: DateValues, series: IReadSeriesConfiguration): TagDateValue {
    const retVal: TagDateValue = {
        guid: series.guid,
        timeStamp: dateValues.timestamp,
        value: dateValues.value,
        alarm: dateValues.alarm,
        comment: dateValues.comment,
        quality: dateValues.quality,
        secondaryValue: dateValues.secondaryValue,
        valueType: series.value,
        target: dateValues.target !== null ? dateValues.target : undefined,
        count: dateValues.count
    };
    if (series.offset && series.offset.interval) {
        retVal.offset = series.offset;
        retVal.timeStamp = moment(retVal.timeStamp).subtract(toDuration(series.offset)).toISOString();
    } else {
        retVal.timeStamp = new Date(retVal.timeStamp as string).toISOString();
    }
    return retVal;
}

function toWidgetValues(
    readResult: WidgetReadResult,
    config: WidgetReadConfiguration,
    executedQueries: WidgetQueryResults
) {
    const series = getSeries(config, executedQueries);

    const aggregatedValue: TagDateValue[] = [];
    const contexts: KeyValuePair<string, string>[] = [];
    const tendencies: KeyValuePair<string, number>[] = [];
    const missing: KeyValuePair<string, any[]>[] = [];
    const statuses: KeyValuePair<string, number>[] = [];
    const alarms: KeyValuePair<string, number>[] = [];

    let aggregatedValues: TagDateValue[] = [];
    let values: TagDateValue[] = [];

    for (const seriesId of Object.keys(readResult.seriesValues)) {
        const originalSeries = _.find(series, s => s.id === +seriesId);
        if (originalSeries) {
            const seriesValues = readResult.seriesValues[seriesId];
            values = fillArrays(seriesValues, aggregatedValue, originalSeries, contexts, tendencies, statuses, alarms, values, missing);
            // If the same series and value type is added twice to the widget...
            const existingKeys = aggregatedValues
                .map(getKey);
            const newAggregatedValues = seriesValues.aggregatedValues
                .map(d => toTagDateValue(d, originalSeries))
                .filter(d => existingKeys.indexOf(getKey(d)) < 0);
            aggregatedValues = aggregatedValues.concat(newAggregatedValues);
        }
    }
    values = values.sort((a, b) => a.timeStamp < b.timeStamp ? -1 : 1);
    aggregatedValues = aggregatedValues.sort((a, b) => a.timeStamp < b.timeStamp ? -1 : 1);
    const wv: WidgetValues = {
        aggregatedValue,
        aggregatedValues,
        alarms,
        beginDate: config.configuration.beginDate,
        contexts,
        endDate: config.configuration.endDate,
        entries: readResult.logbookEntries,
        guid: config.guid,
        missing,
        statuses,
        tendencies,
        values,
        widgetReadResult: readResult
    };
    return wv;
}

function fillArrays(seriesValues: SerieValues, aggregatedValue: TagDateValue[], originalSeries: IReadSeriesConfiguration, contexts: KeyValuePair<string, string>[], tendencies: KeyValuePair<string, number>[], statuses: KeyValuePair<string, number>[], alarms: KeyValuePair<string, number>[], values: TagDateValue[], missing: KeyValuePair<string, any[]>[]) {
    if (seriesValues.aggregatedValue) {
        aggregatedValue.push(toTagDateValue(seriesValues.aggregatedValue, originalSeries));
        if (!_.find(contexts, m => m.key === originalSeries.guid)) {
            contexts.push({ key: originalSeries.guid, value: seriesValues.aggregatedValue.context });
        }
        if (!_.find(tendencies, m => m.key === originalSeries.guid)) {
            tendencies.push({ key: originalSeries.guid, value: seriesValues.aggregatedValue.tendency });
        }
    }
    if (!_.find(statuses, m => m.key === originalSeries.guid)) {
        statuses.push({ key: originalSeries.guid, value: seriesValues.resultStatus });
    }
    if (!_.find(alarms, m => m.key === originalSeries.guid)) {
        alarms.push({ key: originalSeries.guid, value: seriesValues.alarm });
    }
    values = values.concat(seriesValues.values.map(d => toTagDateValue(d, originalSeries)));
    if (!_.find(missing, m => m.key === originalSeries.guid)) {
        missing.push({
            key: originalSeries.guid,
            value: seriesValues.missing
        });
    }
    return values;
}

function getSeries(config: WidgetReadConfiguration, executedQueries: WidgetQueryResults) {
    let series = config.configuration.series.filter(s => !s.isQuery);
    if (executedQueries) {
        series = series.concat(getQueryQeries(executedQueries, config));
    }
    if (config.configuration.extraValues && config.configuration.extraValues.length) {
        config.configuration.extraValues.forEach(ev => series = series.concat(ev.series));
    }
    return series;
}

function getQueryQeries(executedQueries: WidgetQueryResults, config: WidgetReadConfiguration) {
    return Object.keys(executedQueries)
        .map(key => {
            const queryConfig = _.find(config.configuration.series, s => s.id === executedQueries[key].originalSerieId);
            return {
                guid: executedQueries[key].valueViewModelGuid,
                value: queryConfig.value,
                offset: queryConfig.offset,
                id: +key,
                isBoundToGroup: false,
                isBoundIncludehildren: false,
                isQuery: false
            };
        });
}

export function mapDashboardReadResultDto(
    data: DashboardReadResultDto,
    widgetConfigurationList: { guid: string; configuration: WidgetValuesReadConfiguration }[]
) {
    let result: WidgetValues[] = [];
    if (data && data.widgetResults) {
        const widgetResults: WidgetValues[] = getWidgetResults(data, widgetConfigurationList);
        result = result.concat(widgetResults);

        const errorResults: WidgetValues[] = getErrorResults(result, data);
        result = result.concat(errorResults);
    } else {
        console.error('Can\'t retrieve data from API. (Empty array in mapDashboardReadResultDto)');
    }
    return result;
}

function getErrorResults(result: WidgetValues[], data: DashboardReadResultDto) {
    const errorResults: WidgetValues[] = [];
    // Retrieve widgets in error
    const index = toDictionary(result);
    Object.keys(data.errors)
        .forEach(guid => {
            if (index[guid]) {
                index[guid].errors = data.errors[guid];
            } else {
                const values: WidgetValues = {
                    aggregatedValue: [],
                    aggregatedValues: [],
                    alarms: [],
                    beginDate: undefined,
                    contexts: [],
                    endDate: undefined,
                    errors: data.errors[guid],
                    guid,
                    missing: [],
                    statuses: [],
                    tendencies: [],
                    values: []
                };
                errorResults.push(values);
            }
        });
    return errorResults;
}

function getWidgetResults(data: DashboardReadResultDto, widgetConfigurationList: { guid: string; configuration: WidgetValuesReadConfiguration; }[]) {
    const widgetResults: WidgetValues[] = [];
    for (const key of Object.keys(data.widgetResults)) {
        try {
            const config = _.find(widgetConfigurationList, w => w.guid === key);
            if (config) {
                if (config.configuration.series || (config.configuration.extraValues && config.configuration.extraValues.length)) {
                    const readResult = data.widgetResults[key];
                    const values: WidgetValues = toWidgetValues(readResult, config, data.executedQueries?.map[key]);
                    widgetResults.push(values);
                }
            }
        }
        catch (e) {
            console.error(e);
        }
    }
    return widgetResults;
}
